/**
 * Aba "Custos" — visao financeira do projeto em R$.
 *
 * Stats no topo (estimado / realizado / extras / total), drill-down por
 * fase, lista de custos adicionais com CRUD. Avisa quando ha fases sem
 * taxa de custo (responsavel sem R$/h cadastrado pelo admin).
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { DollarSign, TrendingUp, AlertTriangle, Plus, Trash2, FileText } from 'lucide-react';
import { projetosAPI } from '../../../services/inovacaoAPI';
import { mensagemErro } from '../labels';

const TIPOS = [
  { v: 'licenca',       l: 'Licença' },
  { v: 'terceirizado',  l: 'Terceirizado' },
  { v: 'viagem',        l: 'Viagem' },
  { v: 'hardware',      l: 'Hardware' },
  { v: 'consultoria',   l: 'Consultoria' },
  { v: 'outros',        l: 'Outros' },
];

export default function CustosWS({ projeto }) {
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [novoTipo, setNovoTipo] = useState('outros');
  const [novoDescricao, setNovoDescricao] = useState('');
  const [novoValor, setNovoValor] = useState('');
  const [salvando, setSalvando] = useState(false);

  function carregar() {
    setLoading(true);
    projetosAPI.custos(projeto.id)
      .then(setResumo)
      .catch((e) => toast.error(mensagemErro(e, 'Erro ao carregar custos')))
      .finally(() => setLoading(false));
  }

  useEffect(() => { carregar(); }, [projeto.id]);

  async function adicionarCustoExtra(e) {
    e.preventDefault();
    if (!novoDescricao.trim() || !novoValor || parseFloat(novoValor) <= 0) {
      toast.error('Preencha descrição e valor positivo'); return;
    }
    setSalvando(true);
    try {
      await projetosAPI.criarCustoAdicional(projeto.id, {
        descricao: novoDescricao.trim(),
        valor_brl: parseFloat(novoValor),
        tipo: novoTipo,
      });
      toast.success('Custo adicional registrado');
      setNovoDescricao(''); setNovoValor(''); setNovoTipo('outros');
      carregar();
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao adicionar custo'));
    }
    setSalvando(false);
  }

  async function removerExtra(id) {
    if (!window.confirm('Remover este custo adicional?')) return;
    try {
      await projetosAPI.removerCustoAdicional(id);
      toast.success('Removido');
      carregar();
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao remover'));
    }
  }

  if (loading) return <p style={{ color: '#7f8c8d' }}>Carregando…</p>;
  if (!resumo) return null;

  const deltaCusto = parseFloat(resumo.custo_total_realizado_brl) - parseFloat(resumo.custo_total_estimado_brl);

  return (
    <div>
      {/* Stats grandes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 16 }}>
        <StatCusto Icon={DollarSign} label="Total realizado" valor={resumo.custo_total_realizado_brl}
                   destaque cor="var(--brand, #20a490)"
                   sub={`(${formatarBRL(resumo.custo_horas_realizado_brl)} horas + ${formatarBRL(resumo.custo_adicional_total_brl)} extras)`} />
        <StatCusto Icon={FileText} label="Total estimado" valor={resumo.custo_total_estimado_brl} cor="#3498db"
                   sub={`${parseFloat(resumo.horas_estimadas_total).toFixed(1)}h estimadas`} />
        <StatCusto Icon={TrendingUp} label="Δ vs estimado" valor={deltaCusto}
                   cor={deltaCusto >= 0 ? '#e74c3c' : '#27ae60'} mostrarSinal />
        <StatCusto Icon={Plus} label="Custos adicionais" valor={resumo.custo_adicional_total_brl} cor="#9b59b6"
                   sub={`${resumo.custos_adicionais.length} item(s)`} />
      </div>

      {resumo.fases_sem_taxa.length > 0 && (
        <div style={{ background: '#fff4e6', border: '1px solid #ffd6a8', padding: 12,
                      borderRadius: 8, marginBottom: 16, fontSize: '0.88rem',
                      display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={18} color="#e67e22" />
          <div>
            <b style={{ color: '#d35400' }}>{resumo.fases_sem_taxa.length} fase(s) sem taxa de custo.</b>
            <span style={{ color: '#7f8c8d', marginLeft: 6 }}>
              Esses responsáveis não têm R$/h cadastrado pelo admin, então o custo dessas fases não entra no total.
            </span>
          </div>
        </div>
      )}

      {/* Drill-down por fase */}
      <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: 16 }}>
        <h4 style={{ margin: 0, padding: '12px 16px', borderBottom: '1px solid #ecf0f1',
                     fontSize: '0.85rem', color: '#2c3e50' }}>
          Custo por fase
        </h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead style={{ background: '#f8f9fa' }}>
            <tr>
              <th style={th}>Fase</th>
              <th style={th}>R$/h</th>
              <th style={th}>Horas (real / est)</th>
              <th style={th}>Custo estimado</th>
              <th style={th}>Custo realizado</th>
            </tr>
          </thead>
          <tbody>
            {resumo.fases.map((f) => (
              <tr key={f.fase_id} style={{ borderTop: '1px solid #ecf0f1' }}>
                <td style={td}>{f.nome}</td>
                <td style={td}>
                  {f.valor_hora_brl ? formatarBRL(f.valor_hora_brl) : (
                    <span style={{ color: '#e67e22' }}>sem taxa</span>
                  )}
                </td>
                <td style={td}>{parseFloat(f.horas_realizadas).toFixed(1)} / {parseFloat(f.horas_estimadas).toFixed(1)}</td>
                <td style={td}>{formatarBRL(f.custo_estimado_brl)}</td>
                <td style={td}>{formatarBRL(f.custo_realizado_brl)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Custos adicionais — lista + form */}
      <div style={{ background: '#fff', borderRadius: 10, padding: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#2c3e50' }}>
          Custos adicionais (licenças, terceiros, viagens…)
        </h4>

        <form onSubmit={adicionarCustoExtra}
              style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 12 }}>
          <input value={novoDescricao} onChange={(e) => setNovoDescricao(e.target.value)}
                 placeholder="Descrição (ex: Licença Figma anual)" style={inputStyle} maxLength={255} />
          <input type="number" min="0.01" step="0.01" value={novoValor}
                 onChange={(e) => setNovoValor(e.target.value)} placeholder="R$" style={inputStyle} />
          <select value={novoTipo} onChange={(e) => setNovoTipo(e.target.value)} style={inputStyle}>
            {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <button type="submit" disabled={salvando}
                  style={{ background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', border: 'none',
                           borderRadius: 4, padding: '8px 16px', cursor: 'pointer',
                           opacity: salvando ? 0.6 : 1, fontSize: '0.85rem' }}>
            <Plus size={14} /> Adicionar
          </button>
        </form>

        {resumo.custos_adicionais.length === 0 ? (
          <p style={{ color: '#bdc3c7', fontSize: '0.85rem', margin: 0 }}>Nenhum custo adicional ainda.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {resumo.custos_adicionais.map((c) => (
              <li key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                                       padding: '8px 0', borderTop: '1px solid #ecf0f1', fontSize: '0.85rem' }}>
                <span style={{ background: '#ecf0f1', padding: '2px 8px', borderRadius: 10,
                               fontSize: '0.7rem', color: '#555' }}>
                  {TIPOS.find((t) => t.v === c.tipo)?.l || c.tipo}
                </span>
                <span style={{ flex: 1, color: '#2c3e50' }}>{c.descricao}</span>
                <span style={{ color: '#7f8c8d', fontSize: '0.75rem' }}>{formatarDataBR(c.data)}</span>
                <span style={{ fontWeight: 600, color: '#2c3e50', minWidth: 100, textAlign: 'right' }}>
                  {formatarBRL(c.valor_brl)}
                </span>
                <button onClick={() => removerExtra(c.id)} title="Remover"
                        style={{ background: 'transparent', border: 'none', color: '#e74c3c',
                                 cursor: 'pointer', padding: 4 }}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCusto({ Icon, label, valor, cor, sub, destaque, mostrarSinal }) {
  const n = parseFloat(valor || 0);
  const sinal = mostrarSinal && n > 0 ? '+' : '';
  return (
    <div style={{ background: '#fff', padding: 14, borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  borderLeft: destaque ? `4px solid ${cor}` : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: cor }}>
        <Icon size={14} />
        <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#2c3e50', marginTop: 4 }}>
        {sinal}{formatarBRL(n)}
      </div>
      {sub && <div style={{ fontSize: '0.72rem', color: '#7f8c8d', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function formatarBRL(v) {
  const n = parseFloat(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarDataBR(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

const th = { textAlign: 'left', padding: '10px 12px', fontSize: '0.72rem',
             color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600 };
const td = { padding: '10px 12px' };
const inputStyle = { padding: 8, border: '1px solid #ddd', borderRadius: 4,
                    fontSize: '0.85rem', fontFamily: 'inherit' };
